import React, { useEffect, useState } from "react";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { toast } from "react-toastify";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "../../../components/ui/sheet"
import { Textarea } from "../../../components/ui/textarea"
import { Package, Loader2, X } from "lucide-react"
import { useCategoryManagement } from "../../Services/CategoryManagement"
import { inputFieldValidator } from "../../../Utilities/validation";
import { isApiError, getApiErrorMessage } from "../../../Utilities/apiResponse";

const AddProductCategoryDialog = ({
  isOpen,
  onClose,
  selectedCategory,
  onSuccessCallBack,
  isUpdate,
  setIsUpdate
}: any) => {

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState({ categoryName: false })

  const { addCategory, updateCategory } = useCategoryManagement();

  const [attributes, setAttributes] = useState({
    description: "",
    categoryName: ""
  });

  useEffect(() => {
    if (isUpdate && selectedCategory) {
      setAttributes({
        description: selectedCategory?.description || "",
        categoryName: selectedCategory?.categoryName || ""
      });
      setFieldErrors({ categoryName: false });
    }
  }, [isUpdate, selectedCategory]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, type: string) => {
    const value = e.target.value;
    setAttributes((prev) => ({ ...prev, [type]: value }));
    if (type === "categoryName" && value.trim() !== "") {
      setFieldErrors((prev) => ({ ...prev, categoryName: false }));
    }
  };

  const handleSubmit = async () => {
    const categoryNameInvalid = inputFieldValidator(attributes.categoryName, "string");
    if (categoryNameInvalid) {
      setFieldErrors({ categoryName: true });
      return;
    }

    setLoading(true);
    setError(null);
    setFieldErrors({ categoryName: false });

    const data = { ...attributes };

    try {
      if (isUpdate) {
        const payload = { id: selectedCategory?.id, ...data };
        const response = await updateCategory(payload);
        if (isApiError(response)) {
          toast.error(getApiErrorMessage(response) || "Failed to update Product Category");
          setLoading(false);
          return;
        }
        toast.success("Product Category updated.");
        onSuccessCallBack();
        handleClose();
        return;
      }

      const response = await addCategory(data);
      if (isApiError(response)) {
        toast.error(getApiErrorMessage(response) || "Failed to add Product Category");
        setLoading(false);
        return;
      }
      toast.success("Product Category added");
      onSuccessCallBack();
      handleClose();
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : "Failed to save category");
      toast.error("Failed to save category");
    }
  }

  const resetForm = () => {
    setAttributes({
      categoryName: "",
      description: ""
    });
    setError(null);
    setFieldErrors({ categoryName: false });
    setLoading(false);
    setIsUpdate(false);
  }

  const handleClose = () => {
    resetForm();
    onClose();
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full min-w-[600px] p-0 overflow-auto">
        <SheetHeader className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 border-b detail-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Package className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-bold text-gray-900">{isUpdate ? 'Edit' : 'Add New'} Product Category</SheetTitle>
                <p className="text-sm text-gray-600 mt-1">Configure base properties for category mapping</p>
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={handleClose} className="close-panel">
            <X className="h-4 w-4" />
          </Button>
        </SheetHeader>

        <div className="p-6 space-y-6 flex-1 overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-950 border border-red-800 rounded text-red-200 text-sm">{error}</div>
          )}

          <div className="space-y-4 mb-6">
            <div>
              <Label htmlFor="categoryName" className="text-sm font-medium text-gray-700">
                Category Name *
              </Label>
              <Input
                id="categoryName"
                placeholder="Enter category name"
                value={attributes?.categoryName}
                onChange={(e) => onChange(e, "categoryName")}
                disabled={loading}
                className={`w-full mt-1 ${fieldErrors.categoryName ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              />
              {fieldErrors.categoryName && (
                <p className="text-sm text-red-500 mt-1">Category name is required</p>
              )}
            </div>

            <div>
              <Label htmlFor="description" className="text-sm font-medium text-gray-700">
                Description
              </Label>
              <Textarea
                id="description"
                placeholder="Enter category description"
                value={attributes?.description}
                onChange={(e) => onChange(e, "description")}
                disabled={loading}
                rows={4}
                className="w-full mt-1"
              />
            </div>
          </div>
        </div>

        <SheetFooter>
          <div className="absolute bottom-0 left-0 right-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-3">
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isUpdate ? "Updating..." : "Adding..."}
                </>
              ) : (
                isUpdate ? "Update Category" : "Add Category"
              )}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default AddProductCategoryDialog;
